import { useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client/react';
import * as R from 'ramda';

import WorkTemplateTree from '@atom/components/common/workTemplateTree/WorkTemplateTree';
import { GET_WORK_ORDER_TEMPLATE_FOLDERS } from '@atom/graph/workTemplate';
import { Button, Modal, Progress } from '@atom/mui';
import colors from '@atom/styles/colors';
import fonts from '@atom/styles/fonts';
import { WorkOrderTemplateFolder } from '@atom/types/workTemplate';
import { flattenTreeChildren } from '@atom/utilities/treeUtilities';

import classes from './filterPanel.module.css';

const styles = {
  button: {
    minWidth: 0,
    padding: 0,
    fontSize: fonts.sm,
    fontWeight: 'normal',
    textTransform: 'capitalize',
  },
  progress: {
    justifyContent: 'flex-start',
  },
  modalContent: {
    height: '50vh',
    padding: '0',
  },
};

type TemplateFolderFilterProps = {
  workTemplateFolderIds: Set<string>;
  toggled?: boolean;
  onWorkTemplateFoldersUpdate: (ids: Set<string>) => void;
};

const TemplateFolderFilter = ({
  workTemplateFolderIds,
  toggled,
  onWorkTemplateFoldersUpdate,
}: TemplateFolderFilterProps) => {
  const [selected, setSelected] = useState<WorkOrderTemplateFolder[]>([]);
  const [applied, setApplied] = useState<WorkOrderTemplateFolder[]>([]);
  const [open, setOpen] = useState<boolean>(false);

  const [getWorkTemplateFolders, { loading }] = useLazyQuery(
    GET_WORK_ORDER_TEMPLATE_FOLDERS,
    { fetchPolicy: 'no-cache' },
  );

  useEffect(() => {
    if (workTemplateFolderIds.size === 0) {
      return;
    }

    const fetchFolders = async () => {
      try {
        const { data } = await getWorkTemplateFolders({
          variables: {
            input: {
              ids: Array.from(workTemplateFolderIds),
            },
          },
        });

        const folders =
          data?.workOrderTemplateFolders?.workOrderTemplateFolders ?? [];
        setSelected(folders);
        setApplied(folders);
      } catch {
        return;
      }
    };

    fetchFolders();
  }, [workTemplateFolderIds]);

  useEffect(() => {
    const workTemplateFolders = selected.filter(({ id }) =>
      workTemplateFolderIds.has(id),
    );

    setSelected(workTemplateFolders);
    setApplied(workTemplateFolders);
  }, [workTemplateFolderIds]);

  const handleToggle = (folder?: WorkOrderTemplateFolder) => {
    const isSelected = selected.some(({ id }) => folder.id === id);

    setSelected(
      isSelected
        ? selected.filter(({ id }) => id !== folder.id)
        : [...selected, folder],
    );
  };

  const getTopLevelFolders = (folders: WorkOrderTemplateFolder[]): string[] => {
    const subfolders = folders.reduce(
      (prev, folder) => R.uniq([...prev, ...flattenTreeChildren(folder)]),
      [],
    );

    return R.without(
      subfolders.map(({ id }) => id),
      folders.map(({ id }) => id),
    );
  };

  const handleSave = () => {
    const rootSelected = selected.some(folder => folder.id === 'workTypes');
    const selectedIds = rootSelected
      ? ['workTypes']
      : getTopLevelFolders(selected);

    onWorkTemplateFoldersUpdate(new Set(selectedIds));
    setOpen(false);
  };

  const handleReset = () => {
    onWorkTemplateFoldersUpdate(new Set([]));
  };

  const handleClear = () => {
    setSelected([]);
  };

  const handleCancel = () => {
    setSelected(applied);
    setOpen(false);
  };

  const selectedIds = new Set(selected.map(({ id }) => id));
  const workTemplateFolderDisplay = applied
    .filter(({ id }) => workTemplateFolderIds.has(id))
    .map(({ name }) => name)
    .join(', ');

  const buttonStyles = {
    ...styles.button,
    color: toggled ? colors.brand.blue : colors.neutral.gray,
  };

  return (
    <>
      <div className={classes.filterContainerLabel}>Work Template Folder</div>
      <div className={classes.buttonContainer}>
        <Button
          style={buttonStyles}
          size="small"
          onClick={() => setOpen(true)}
          disabled={loading || !toggled}
          data-cy="mapFilterWorkTemplateFolderSelect"
        >
          select
        </Button>
        {workTemplateFolderIds.size > 0 && (
          <>
            <span className={classes.buttonDivider}>|</span>
            <Button
              style={{ ...buttonStyles, marginRight: '1rem' }}
              size="small"
              disabled={loading || !toggled}
              onClick={handleReset}
              data-cy="mapFilterFolderClear"
            >
              clear all
            </Button>
          </>
        )}
      </div>
      <div className={classes.filterValues}>
        {loading && <Progress size={20} style={styles.progress} />}
        {workTemplateFolderIds.has('workTypes')
          ? 'All Work Folders'
          : workTemplateFolderDisplay}
      </div>
      <Modal
        title="Select Work Template Folder"
        open={open}
        onCancel={handleCancel}
        onConfirm={handleSave}
        onClear={handleClear}
        contentStyle={styles.modalContent}
        confirmButtonText="Ok"
      >
        <WorkTemplateTree
          onFolderToggle={folder => handleToggle(folder)}
          getFolderSelected={folder => selectedIds.has(folder.id)}
          autoSelectChildFolders
          hideTemplates
          includeSearch={false}
        />
      </Modal>
    </>
  );
};

export default TemplateFolderFilter;
